import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';

export default function UploadClip() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [gamemode, setGamemode] = useState('sword');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState<number>(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | null>(null);

  const [canUpload, setCanUpload] = useState<boolean | null>(null);
  
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const allowed = ['video/mp4', 'video/webm', 'video/quicktime'];
      if (!allowed.includes(file.type)) {
        setError('Only .mp4, .webm and .mov files are supported');
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        setError('Video file size exceeds the 50MB limit');
        return;
      }
      setVideoFile(file);
      const url = URL.createObjectURL(file);
      setVideoPreviewUrl(url);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const allowed = ['video/mp4', 'video/webm', 'video/quicktime'];
      if (!allowed.includes(file.type)) {
        setError('Only .mp4, .webm and .mov files are supported');
        return;
      }
      setVideoFile(file);
      const url = URL.createObjectURL(file);
      setVideoPreviewUrl(url);
      setError(null);
    }
  };

  useEffect(() => {
    // Check backend whether user can upload today
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/api/clips/can-upload');
        if (!mounted) return;
        setCanUpload(res.data?.canUpload ?? true);
        if (res.data?.canUpload === false) setError(res.data?.message || 'You can only upload 1 highlight per day');
      } catch (err: any) {
        // If error, allow form but show no blocking message
        setCanUpload(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!videoPreviewUrl) {
      setVideoDuration(null);
      setVideoCurrentTime(0);
      return;
    }
    const v = videoRef.current;
    if (!v) return;
    const onLoaded = () => {
      const dur = v.duration || 0;
      setVideoDuration(dur);
      // enforce duration limits
      if (dur < 7) setError('Video must be at least 7 seconds long');
      else if (dur > 240) setError('Video cannot be longer than 4 minutes');
      else setError(null);
    };
    const onTime = () => setVideoCurrentTime(v.currentTime || 0);
    v.addEventListener('loadedmetadata', onLoaded);
    v.addEventListener('timeupdate', onTime);
    return () => {
      v.removeEventListener('loadedmetadata', onLoaded);
      v.removeEventListener('timeupdate', onTime);
    };
  }, [videoPreviewUrl]);

  const handleThumbnailCapture = async () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth || 640;
    canvas.height = v.videoHeight || 360;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), 'image/jpeg', 0.92));
    if (!blob) return;
    const file = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });
    setThumbnailFile(file);
    setThumbnailPreviewUrl(URL.createObjectURL(file));
  };

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const f = e.target.files[0];
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(f.type)) {
      setError('Only .jpg, .png and .webp thumbnails are supported');
      return;
    }
    setThumbnailFile(f);
    setThumbnailPreviewUrl(URL.createObjectURL(f));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canUpload) {
      setError('You can only upload 1 highlight per day');
      return;
    }
    if (!title || !gamemode || !videoFile || !thumbnailFile) {
      setError('Title, gamemode, video file, and thumbnail are required');
      return;
    }
    if (title.length > 80) {
      setError('Title cannot exceed 80 characters');
      return;
    }
    if (description.length > 300) {
      setError('Description cannot exceed 300 characters');
      return;
    }
    if (videoDuration !== null && (videoDuration < 7 || videoDuration > 240)) {
      // safety check in case duration wasn't enforced earlier
      setError('Video must be between 7 seconds and 4 minutes in length');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('gamemode', gamemode);
    formData.append('video', videoFile);
    formData.append('thumbnail', thumbnailFile as Blob);

    try {
      await api.post('/api/clips', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || videoFile.size;
          const current = progressEvent.loaded;
          const percentage = Math.round((current / total) * 100);
          setUploadProgress(percentage);
        }
      });

      // Clear form
      setTitle('');
      setDescription('');
      setVideoFile(null);
      setUploadProgress(100);

      // Redirect to Highlights
      navigate('/highlights');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to upload video clip.';
      setError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const gamemodes = [
    { value: 'sword', label: 'Sword' },
    { value: 'mace', label: 'Mace' },
    { value: 'mace_rockets', label: 'Mace Rockets' },
    { value: 'cpvp', label: 'Crystal PvP' },
    { value: 'axe_shield', label: 'Axe & Shield' },
    { value: 'nethpot', label: 'Nethpot' },
    { value: 'tank', label: 'Tank' }
  ];

  return (
    <div className="flex-1 min-h-screen bg-background text-on-surface">
      <div className="mb-8 select-none">
        <h1 className="font-headline-lg text-headline-lg text-on-surface mb-2">Upload Showcase</h1>
        <p className="text-on-surface-variant text-body-sm">
          Publish your gameplay clips to the community highlights page.
        </p>
      </div>

      {error && (
        <div className="p-3 mb-6 bg-error/10 border border-error/20 text-error rounded text-[13px] font-medium flex items-center gap-2 select-none">
          <span className="material-symbols-outlined text-[16px]">error</span>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-12 gap-gutter select-none">
        {/* Left Drag & Drop Selection Column */}
        <div className="col-span-12 lg:col-span-5 space-y-6">
          <label className="block text-[11px] uppercase tracking-wider text-on-surface-variant font-bold">
            Video Clip
          </label>
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-white/10 rounded-lg aspect-square flex flex-col items-center justify-center bg-[#141414] hover:bg-[#1a1a1a] hover:border-error/50 transition-all cursor-pointer p-6 text-center select-none"
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="video/*"
              className="hidden"
            />
            {videoFile ? (
              <div className="space-y-4">
                <span className="material-symbols-outlined text-[64px] text-green-500">
                  check_circle
                </span>
                <div className="space-y-1">
                  <div className="text-on-surface font-semibold truncate max-w-[200px]">
                    {videoFile.name}
                  </div>
                  <div className="text-[11px] text-on-surface-variant font-mono">
                    {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                  </div>
                </div>
                <div className="text-[11px] text-[#e03c3c] font-semibold hover:underline">
                  Click to replace file
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <span className="material-symbols-outlined text-[64px] text-on-surface-variant">
                  cloud_upload
                </span>
                <div>
                  <div className="text-on-surface font-semibold text-body-sm">
                    Drag and drop file here
                  </div>
                  <div className="text-[12px] text-on-surface-variant mt-1">
                    or click to browse local files
                  </div>
                </div>
                <div className="text-[10px] text-outline font-mono uppercase">
                  MP4, WebM • Max 50MB
                </div>
              </div>
            )}
          </div>

          {videoPreviewUrl && (
            <div className="mt-4 space-y-3">
              <video
                src={videoPreviewUrl}
                ref={videoRef}
                controls
                className="w-full rounded-md bg-black"
              />

              {videoDuration && (
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={Math.max(0, Math.floor(videoDuration))}
                    value={Math.floor(videoCurrentTime)}
                    onChange={(e) => {
                      const t = Number(e.target.value);
                      if (videoRef.current) videoRef.current.currentTime = t;
                      setVideoCurrentTime(t);
                    }}
                    className="flex-1"
                  />
                  <div className="text-[11px] text-on-surface-variant w-24 text-right font-mono">
                    {Math.floor(videoCurrentTime)}s / {Math.floor(videoDuration)}s
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleThumbnailCapture}
                  className="px-3 py-1.5 rounded bg-primary/10 text-primary text-[13px]"
                >
                  Capture Thumbnail
                </button>

                <label className="text-[13px]">
                  <input type="file" accept="image/*" onChange={handleThumbnailUpload} className="hidden" />
                  <span className="px-3 py-1.5 rounded bg-white/5 cursor-pointer">Upload Custom Thumbnail</span>
                </label>

                {thumbnailPreviewUrl && (
                  <img src={thumbnailPreviewUrl} alt="thumbnail" className="w-20 h-12 object-cover rounded ml-auto" />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Form Info Column */}
        <div className="col-span-12 lg:col-span-7 bg-[#141414] thin-border rounded-lg p-gutter space-y-6 h-fit">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-on-surface-variant font-bold mb-2">
              Clip Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Clean 3-Hit Combo Anchor Pop"
              required
              maxLength={80}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-[4px] px-3 py-3 text-on-surface placeholder:text-white/30 outline-none focus:border-error focus:ring-0 transition-all text-body-sm"
            />
            <div className="text-[11px] text-on-surface-variant mt-1 text-right font-mono">{title.length}/80</div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-on-surface-variant font-bold mb-2">
              Description (Optional)
            </label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the combat timing, spacing, or technique..."
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-[4px] px-3 py-3 text-on-surface placeholder:text-white/30 outline-none focus:border-error focus:ring-0 transition-all text-body-sm resize-none"
              maxLength={300}
            />
            <div className="text-[11px] text-on-surface-variant mt-1 text-right font-mono">{description.length}/300</div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-on-surface-variant font-bold mb-3">
              Course Gamemode
            </label>
            <div className="flex flex-wrap gap-2">
              {gamemodes.map((mode) => {
                const isSelected = gamemode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setGamemode(mode.value)}
                    className={`px-3 py-1.5 rounded-[4px] text-[12px] font-semibold border transition-all ${
                      isSelected
                        ? 'border-error/20 bg-error/15 text-error font-bold'
                        : 'border-white/10 bg-transparent text-on-surface-variant hover:text-on-surface hover:border-white/20'
                    }`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
          </div>

          {isUploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-[11px] text-on-surface-variant font-mono">
                <span>Uploading Showcase File</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full h-1 bg-surface-container rounded-full overflow-hidden">
                <div
                  className="h-full bg-error rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          <div className="flex gap-4 pt-4 border-t border-white/5">
            <button
              type="submit"
              disabled={isUploading || !title || !gamemode || !thumbnailFile || canUpload === false}
              className="flex-1 bg-error hover:brightness-110 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none text-white py-3.5 rounded-[4px] font-button text-button uppercase tracking-wider font-bold transition-all"
            >
              {isUploading ? 'Uploading Video...' : 'Publish Highlight'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/highlights')}
              disabled={isUploading}
              className="border border-white/10 hover:bg-white/5 text-on-surface font-button text-button px-6 py-3 rounded-[4px] transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
